import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Droplets, Clock, Shield } from 'lucide-react';

/**
 * QuickMetrics - Barra de métricas rápidas debajo del hero
 * Paleta brand: bone/black con acentos cobalt/magenta
 */
const QuickMetrics = () => {
  const metrics = [
    {
      value: "+ 3,000",
      label: "perfumes vendidos",
      icon: Sparkles,
      color: "text-brand-cobalt"
    },
    {
      value: "Hasta 40%",
      label: "de concentración",
      icon: Droplets,
      color: "text-brand-magenta"
    },
    {
      value: "+8 h",
      label: "de duración",
      icon: Clock,
      color: "text-brand-cobalt"
    },
    {
      value: "30 DÍAS",
      label: "de garantía total",
      icon: Shield,
      color: "text-brand-magenta"
    }
  ];

  // Contenido repetido para que la franja móvil parezca continua
  const repeatedMetrics = [...metrics, ...metrics, ...metrics];

  return (
    <div className="w-full py-4 md:py-5 relative bg-brand-bone border-t border-brand-black/[0.08] border-b border-brand-black/[0.08] overflow-hidden">
      {/* Top highlight line — hybrid: sutil cobalt */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-cobalt/20 to-transparent" />
      {/* Bottom highlight line — hybrid: sutil magenta */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-magenta/20 to-transparent" />

      {/* Desktop / tablet — fila fija con separadores */}
      <div className="hidden md:block max-w-6xl mx-auto px-4 relative z-10">
        <div className="flex justify-between items-center">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <motion.div
                key={index}
                className="flex items-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-center px-6 flex flex-col items-center">
                  <div className={`mb-1 ${metric.color} opacity-80`}>
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="text-2xl lg:text-3xl font-bold tracking-tight text-brand-black">
                    {metric.value}
                  </div>
                  <div className="text-xs uppercase tracking-widest mt-0.5 text-brand-black/50">
                    {metric.label}
                  </div>
                </div>

                {index < metrics.length - 1 && (
                  <div className="w-px h-14 ml-4 md:ml-6 bg-gradient-to-b from-brand-cobalt/20 to-brand-magenta/20" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Mobile — franja que se desliza sola, un renglón compacto por métrica */}
      <div className="md:hidden relative z-10">
        <motion.div
          className="flex items-center whitespace-nowrap"
          animate={{ x: ['0%', '-33.333%'] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
        >
          {repeatedMetrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="flex items-center gap-2 px-5">
                <Icon size={16} strokeWidth={1.5} className={`${metric.color} opacity-80 flex-shrink-0`} />
                <span className="text-base font-bold text-brand-black">{metric.value}</span>
                <span className="text-[10px] uppercase tracking-widest text-brand-black/50">{metric.label}</span>
                <span className="text-brand-black/20 ml-3">•</span>
              </div>
            );
          })}
        </motion.div>

        {/* Gradientes en los bordes para que el corte se vea limpio */}
        <div className="absolute top-0 bottom-0 left-0 w-8 bg-gradient-to-r from-brand-bone to-transparent pointer-events-none" />
        <div className="absolute top-0 bottom-0 right-0 w-8 bg-gradient-to-l from-brand-bone to-transparent pointer-events-none" />
      </div>
    </div>
  );
};

export default QuickMetrics;
